import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { usePreferences } from '@/auth/hooks/usePreferences';
import type { ColumnConfig } from '@/contexts/ColumnVisibilityContext';

export type { ColumnConfig };

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'id', label: 'ID', visible: true },
  { key: 'fecha', label: 'Fecha', visible: true },
  { key: 'otorgantes', label: 'Otorgantes', visible: true },
  { key: 'responsables', label: 'Responsables', visible: true },
  { key: 'garantes', label: 'Garantes', visible: true },
  { key: 'asignados', label: 'Asignados', visible: true },
  { key: 'tipo', label: 'Tipo', visible: true },
  { key: 'detalle', label: 'Detalle', visible: true },
  { key: 'copias', label: 'Copias', visible: true },
  { key: 'factura', label: 'Factura', visible: true },
  { key: 'archivos', label: 'Archivos', visible: true },
  { key: 'indicadores', label: 'Indicadores', visible: true },
  { key: 'acciones', label: 'Acciones', visible: true },
];

interface OtrosTramitesColumnVisibilityContextType {
  columns: ColumnConfig[];
  toggleColumn: (columnKey: string) => void;
  resetColumns: () => void;
  getVisibleColumns: () => ColumnConfig[];
  isColumnVisible: (columnKey: string) => boolean;
}

const OtrosTramitesColumnVisibilityContext = createContext<OtrosTramitesColumnVisibilityContextType | undefined>(undefined);

export const OtrosTramitesColumnVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences, updatePartialPreference, isLoading } = usePreferences();
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const hasLoadedFromPreferences = React.useRef(false);

  const loadFromPreferences = useCallback(() => {
    try {
      const visibilityMap = preferences?.otrosTramites?.columnVisibility;
      if (visibilityMap) {
        return DEFAULT_COLUMNS.map(col => ({
          ...col,
          visible: visibilityMap[col.key] !== undefined ? visibilityMap[col.key] : col.visible,
        }));
      }
    } catch (error) {
      console.warn('Error loading otros-tramites column visibility from preferences:', error);
    }
    return DEFAULT_COLUMNS;
  }, [preferences]);

  const toggleColumn = useCallback((columnKey: string) => {
    setColumns(prev => {
      const updated = prev.map(col =>
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      );

      const visibilityMap = updated.reduce((acc, col) => {
        acc[col.key] = col.visible;
        return acc;
      }, {} as Record<string, boolean>);

      updatePartialPreference('otrosTramites.columnVisibility', visibilityMap);

      return updated;
    });
  }, [updatePartialPreference]);

  const resetColumns = useCallback(() => {
    const visibilityMap = DEFAULT_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.visible;
      return acc;
    }, {} as Record<string, boolean>);

    updatePartialPreference('otrosTramites.columnVisibility', visibilityMap);
    setColumns([...DEFAULT_COLUMNS]);
  }, [updatePartialPreference]);

  const getVisibleColumns = useCallback(() => {
    return columns.filter(col => col.visible);
  }, [columns]);

  const isColumnVisible = useCallback((columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    return column?.visible ?? false;
  }, [columns]);

  useEffect(() => {
    if (!isLoading && preferences?.otrosTramites?.columnVisibility && !hasLoadedFromPreferences.current) {
      hasLoadedFromPreferences.current = true;
      const loadedColumns = loadFromPreferences();
      setColumns(loadedColumns);
    }
  }, [isLoading, preferences, loadFromPreferences]);

  const contextValue: OtrosTramitesColumnVisibilityContextType = {
    columns,
    toggleColumn,
    resetColumns,
    getVisibleColumns,
    isColumnVisible,
  };

  return (
    <OtrosTramitesColumnVisibilityContext.Provider value={contextValue}>
      {children}
    </OtrosTramitesColumnVisibilityContext.Provider>
  );
};

export const useOtrosTramitesColumnVisibility = () => {
  const context = useContext(OtrosTramitesColumnVisibilityContext);
  if (context === undefined) {
    throw new Error('useOtrosTramitesColumnVisibility must be used within OtrosTramitesColumnVisibilityProvider');
  }
  return context;
};
